import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./BrandList.scss";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import Loading from "../../../loading/Loading";
import CloseIcon from "../../../../components/icons/CloseIcon";
import ListTopButtonsAndSearch from "./ListTopButtonsAndSearch";
import BrandTable from "./BrandTable";
import { BrandApi } from "../../../../utils/api/Brand";
import BrandFilter from "../filter/BrandFilter";
import { RootState } from "../../../../redux/store";
import { commonAction } from "../../../../redux/features/commonSlice";

export interface IBrandData {
  _id: string;
  brand_name?: string;
  brand_country?: string;
  status?: string;
  brand_logo?: {
    _id: string;
    compressed_file_path?: string;
    original_file_path?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

let searchTimer: any;

interface stateType {
  isLoading: boolean;
  totalPage: number;
  pageNumber: number;
  totalDocs: number;
  listData: IBrandData[];
  search: string;
}

const BrandList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const resetFilter = useSelector(
    (state: RootState) => state?.common?.resetFilter,
  );

  const [state, setState] = useState<stateType>({
    isLoading: true,
    totalPage: 1,
    pageNumber: 1,
    totalDocs: 0,
    listData: [],
    search: "",
  });

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "00-00-00") return "";
    const parts = dateString.split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateString;
  };

  const formatLabel = (value: string) => {
    if (!value) return "";
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const brand_country = searchParams.get("brand_country") || "";
  const status = searchParams.get("status") || "";
  const start_date = searchParams.get("start_date") || "";
  const end_date = searchParams.get("end_date") || "";

  const filterValues = {
    brand_country,
    status,
    startDate: start_date || "00-00-00",
    endDate: end_date || "00-00-00",
  };

  const isFilterActive =
    filterValues.brand_country ||
    filterValues.status ||
    (filterValues.startDate !== "00-00-00" &&
      filterValues.endDate !== "00-00-00");

  const getListDataApi = async (search: string, page: number) => {
    setState({
      ...state,
      isLoading: true,
    });

    const response = await BrandApi.getBrandList({
      page,
      search,
      brand_country: brand_country,
      status: status,
      startDate: start_date,
      endDate: end_date,
    });

    if (response?.success && response?.data?.success) {
      const {
        docs,
        totalPages,
        page: currentPage,
        totalDocs,
      } = response.data.data;

      setState({
        ...state,
        isLoading: false,
        listData: docs || [],
        totalPage: totalPages || 1,
        pageNumber: currentPage || 1,
        totalDocs: totalDocs || 0,
        search,
      });
    } else {
      setState({
        ...state,
        isLoading: false,
        listData: [],
      });
    }
  };

  const onClickPreview = () => {
    setState({
      ...state,
      pageNumber: state.pageNumber - 1,
    });
  };

  const onClickNext = () => {
    setState({
      ...state,
      pageNumber: state.pageNumber + 1,
    });
  };

  const onChangeSearch = (e: any) => {
    let search = e?.target?.value;
    setState({
      ...state,
      search,
      pageNumber: 1,
    });
  };

  const applyFiltersToUrl = (values: any) => {
    let navigationString = `brand-list?brand_country=${
      values?.brand_country || ""
    }&status=${values?.status || ""}&start_date=${
      values?.startDate === "00-00-00" ? "" : values?.startDate
    }&end_date=${values?.endDate === "00-00-00" ? "" : values?.endDate}`;

    navigate(`/${navigationString}`);
    dispatch(commonAction.resetFilter());
  };

  const handleRemoveCountry = () => {
    applyFiltersToUrl({ ...filterValues, brand_country: "" });
  };

  const handleRemoveStatus = () => {
    applyFiltersToUrl({ ...filterValues, status: "" });
  };

  const handleRemoveDate = () => {
    applyFiltersToUrl({
      ...filterValues,
      startDate: "00-00-00",
      endDate: "00-00-00",
    });
  };

  const handleClearFilters = () => {
    navigate("/brand-list");
    dispatch(commonAction.resetFilter());
  };

  useEffect(() => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      getListDataApi(state?.search, state?.pageNumber);
    }, 500);
  }, [state?.search, state?.pageNumber]);

  useEffect(() => {
    getListDataApi(state?.search, state?.pageNumber);
  }, [resetFilter, searchParams]);

  const startRange = (state.pageNumber - 1) * 30 + 1;
  const endRange = Math.min(state.pageNumber * 30, state.totalDocs);

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="property-bank-list-container">
          <div className="list-heading">
            <p>Brands</p>
          </div>

          <ListTopButtonsAndSearch
            add_btn_name="Add"
            add_btn_link="/brand/create"
            reset_btn_link="/brand-list"
            placeholder="Search by name"
            onChangeSearch={onChangeSearch}
            type="brand"
          />

          {isFilterActive && (
            <div className="applied-filters-container">
              <p className="applied-label font-inter-400">Applied filters</p>

              <div className="chips-wrapper">
                {filterValues.brand_country && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Country: {formatLabel(filterValues.brand_country)}
                    </span>
                    <div
                      className="close-btn font-inter-400"
                      onClick={handleRemoveCountry}
                    >
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.status && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Status: {formatLabel(filterValues.status)}
                    </span>
                    <div className="close-btn" onClick={handleRemoveStatus}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.startDate !== "00-00-00" && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Date created: {formatDate(filterValues.startDate)} -{" "}
                      {formatDate(filterValues.endDate)}
                    </span>
                    <div className="close-btn" onClick={handleRemoveDate}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                <div
                  className="clear-all-btn font-inter-400"
                  onClick={handleClearFilters}
                >
                  Clear all filters
                </div>
              </div>
            </div>
          )}

          {state.isLoading ? (
            <div className="data-not-found">
              <Loading />
            </div>
          ) : state.listData.length === 0 ? (
            <div className="data-not-found">
              <p>List is Empty!</p>
            </div>
          ) : (
            <BrandTable listData={state.listData} />
          )}

          {!state.isLoading && state.listData.length > 0 && (
            <div className="pagination-wrapper">
              <div className="viewing-text">
                Viewing {state.totalDocs > 0 ? startRange : 0}-{endRange} of{" "}
                {state.totalDocs} results
              </div>

              <div className="custom-pagination">
                <button
                  className="page-btn"
                  disabled={state.pageNumber === 1}
                  onClick={onClickPreview}
                >
                  ‹
                </button>

                <button
                  className="page-btn"
                  disabled={state.pageNumber === state.totalPage}
                  onClick={onClickNext}
                >
                  ›
                </button>
              </div>
            </div>
          )}

          <BrandFilter />
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default BrandList;
